import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ButtonType } from '../form-field-buttons/form-field-buttons.component';
import { CoreComponentService, Style } from '@app/core/services/core-component.service';
import { LayoutService } from '@app/core/services/layout.service';
import { ComponentBaseComponent } from '../component-base/component-base.component';
import { TranslateService } from '@app/core/services/translate.service';
import { UtilityService } from '@app/core/services/utility.service';
import { GotoParms, StateService } from '@app/core/services/state.service';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';

export interface FormButtonEvent {
    event: MouseEvent;
    buttonId: string;
    options?: { [key: string]: any };
}

@Component({
    selector: 'tt-form-field-button',
    templateUrl: './form-field-button.component.html',
    styleUrls: ['./form-field-button.component.css'],
})
export class FormFieldButtonComponent extends ComponentBaseComponent implements OnInit, OnChanges {
    @Input()
    ttButtonParms?: { [key: string]: any };

    @Input()
    ttId: string = '';

    @Input()
    ttTooltip?: string;

    @Input()
    ttTranslateTooltip?: boolean = false;

    @Input()
    ttIcon: string = '';

    @Input()
    ttType: ButtonType = 'primary';

    @Input()
    ttBeforeGoto?: () => Promise<unknown> | unknown;

    @Input()
    ttGoto?: string = '';

    @Input()
    ttGotoParms?: FormButtonGotoParms;

    @Output()
    ttClick = new EventEmitter<FormButtonEvent>();

    @Input()
    public set ttDisabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
    }
    public get ttDisabled(): boolean {
        return this._disabled;
    }

    private _disabled = false;

    classes = '';

    style: Style = {
        button: {},
        icon: {},
    };

    constructor(private translate: TranslateService, private utility: UtilityService, private state: StateService) {
        super();
    }

    /**
     * Handles click event on the button.
     *
     * @param event the click event.
     */
    public async onButtonClick(event: MouseEvent) {
        console.log(this.ttGoto);
        // if (this.ttButton?.goto && this.ttButton?.gotoParms) {
        //     // Handle goto here sometime
        // } else if (this.ttButton?.onClick && this.ttButton?.onClick instanceof Function) {
        //     this.ttButton.onClick({ event: event, button: this.ttButton, options: this.ttButtonParms });
        // }
        if (this.ttGoto) {
            try {
                if (!!this.ttBeforeGoto) {
                    await this.ttBeforeGoto?.();
                }

                let parameters = this.ttGotoParms;

                if (parameters instanceof Function) {
                    parameters = parameters();
                }

                if (parameters instanceof Promise) {
                    parameters = await parameters;
                }

                if (this.utility.isModifierKeyPressed(event)) {
                    this.state.newTab(this.ttGoto, parameters as GotoParms<string>);
                } else {
                    this.state.go(this.ttGoto, parameters as GotoParms<string>);
                }
            } catch (error) {
                console.error(error);
            }
            // HAndle goto here when stateservice is converted.
        } else if (this.ttClick) {
            this.ttClick.emit({ event: event, buttonId: this.ttId, options: this.ttButtonParms });
        }
    }

    /**
     * Formats the button type.
     *
     * @param button the button to format.
     */
    setButtonType(type: ButtonType) {
        this.classes = 'tt-input__button--' + type;
    }

    async ngOnInit(): Promise<void> {
        this.setButtonType(this.ttType);

        if (!!this.ttTooltip && this.ttTranslateTooltip !== false) {
            this.ttTooltip = await this.translate.translate(this.ttTooltip);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttDisabled']) {
            return (this.ttDisabled = changes['ttDisabled'].currentValue);
        }

        if (changes?.['ttType']?.currentValue) {
            this.setButtonType(changes?.['ttType']?.currentValue);
        }
    }
}

/**
 * Represents a button on a form-control field.
 */
export interface FormButton {
    /** The id of the form field button, not unique. */
    id: string;

    /** The icon to display on the form field button. */
    icon: string;

    /**
     * Tooltip to display on the form button, default is that the tooltip gets translated.
     */
    tooltip?: string;

    /**
     * Whether the tooltip text should be translated, assumes `true` if not set.
     */
    translateTooltip?: boolean;

    /** The type of the button. */
    type: ButtonType;

    /** Functio to call when button is clicked, parameters include a object of the event, button and options (ButtonParms). */
    onClick?: (event: FormButtonEvent) => void;

    /**
     * Function to call before going to new state.
     */
    beforeGoto?(): Promise<unknown> | unknown;

    /** The route to goto. */
    goto?: string;

    /** The parameters to use for the route. */
    gotoParms?: FormButtonGotoParms;

    /** Whether the button should be showed or not. */
    show?: boolean;

    /** Classes to apply to the button. */
    classes?: string;
}

export type FormButtonGotoParms = (() => { [key: string]: string } | string) | { [key: string]: string } | string;
